import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLandingTranslations } from '../contexts/LandingTranslationContext';

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const { translations, language, setLanguage } = useLandingTranslations();
  const navItems = [
    { key: "home", label: translations.nav.home, path: "/" },
    { key: "about", label: translations.nav.about, path: "/about" },
    { key: "services", label: translations.nav.services, path: "/services" },
    { key: "contact", label: translations.nav.contact, path: "/contact" }
  ];
  const productItems = [
    { key: "studentPlatform", label: translations.nav.studentPlatform, action: () => navigate('/signin') }
  ];
  const languages = [
    { key: 'en' as const, label: 'EN' },
    { key: 'zh' as const, label: '中文' }
  ];
  
  const staggerNavItems = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const navItemVariant = {
    hidden: { opacity: 0, y: -20 },
    show: { opacity: 1, y: 0 }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeydown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeydown);
    };
  }, []);

  return (
    <motion.nav
      className="relative z-20 flex justify-between items-center px-8 py-4 backdrop-blur-sm bg-white/30"
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.65, 0, 0.35, 1] }}
    >
      <Link to="/">
        <motion.div
          className="text-[#3D587D] font-bold text-2xl flex items-center cursor-pointer"
          whileHover={{ scale: 1.05 }}
          transition={{ type: "spring", stiffness: 400, damping: 10 }}
        >
          <img src="/logo_transparent.png" alt="ctrlcv logo" className="h-10 w-auto" />
        </motion.div>
      </Link>
      <div className="flex items-center space-x-6">
        <motion.ul
          className="flex space-x-6"
          variants={staggerNavItems}
          initial="hidden"
          animate="show"
        >
          {navItems.map((item) => (
            <Link key={item.key} to={item.path}>
              <motion.li
                variants={navItemVariant}
                whileHover={{
                  scale: 1.1,
                  color: "#407CBD",
                  transition: { type: "spring", stiffness: 400 }
                }}
                className={`cursor-pointer relative group ${
                  location.pathname === item.path ? 'text-[#407CBD]' : ''
                }`}
              >
                {item.label}
                <motion.div
                  className={`absolute -bottom-1 left-0 h-0.5 bg-[#407CBD] ${
                    location.pathname === item.path ? 'w-full' : 'w-0 group-hover:w-full'
                  }`}
                  transition={{ duration: 0.3 }}
                />
              </motion.li>
            </Link>
          ))}
        </motion.ul>
        <div
          className="relative"
          ref={dropdownRef}
        >
          <motion.button
            type="button"
            className="bg-[#407CBD] text-white px-4 py-2 rounded-lg font-medium hover:bg-[#3D587D] transition-colors flex items-center space-x-2"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 0.3 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsDropdownOpen((prev) => !prev)}
            aria-haspopup="menu"
            aria-expanded={isDropdownOpen}
          >
            <span>{translations.nav.otherProducts}</span>
            <span className="text-sm">▾</span>
          </motion.button>
          <AnimatePresence>
            {isDropdownOpen && (
              <motion.ul
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-xl border border-white/60 text-[#3D587D] overflow-hidden z-50 pointer-events-auto"
              >
                {productItems.map((item) => (
                  <li key={item.key}>
                    <button
                      type="button"
                      onClick={() => {
                        item.action();
                        setIsDropdownOpen(false);
                      }}
                      className="block w-full text-left px-4 py-3 hover:bg-[#F6EAD1] transition-colors"
                    >
                      {item.label}
                    </button>
                  </li>
                ))}
              </motion.ul>
            )}
          </AnimatePresence>
        </div>
        <div className="flex items-center bg-white/70 rounded-full p-1 shadow-sm">
          {languages.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => setLanguage(item.key)}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                language === item.key ? 'bg-[#407CBD] text-white' : 'text-[#3D587D]'
              }`}
              aria-pressed={language === item.key}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>
    </motion.nav>
  );
};

export default Navbar; 